import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchVerticalTableData } from 'services/verticalsTablesApi'
import { BaseEntityForTable } from 'utils/types'
import { MRT_SortingState } from 'material-react-table'
import { Filter } from 'components/FilterDrawer/FilterDrawer.config'

interface UseVerticalEntitiesQuery {
  verticalIndices: string
  currentQueryKey: string
  searchValue?: string
  sorting?: MRT_SortingState
  filters?: Filter[]
  vertical?: string
  pageSize?: number
}

export const useVerticalEntitiesQuery = <T extends BaseEntityForTable>({
  verticalIndices,
  currentQueryKey,
  searchValue,
  sorting,
  filters,
  vertical,
  pageSize = 50,
}: UseVerticalEntitiesQuery) => {
  const {
    data: verticalEntities,
    fetchNextPage,
    isFetching,
    isLoading,
    isError,
  } = useInfiniteQuery<T>({
    queryKey: [currentQueryKey, verticalIndices, searchValue, sorting, filters, vertical, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const fromIndex = pageParam * pageSize
      return await fetchVerticalTableData(verticalIndices, fromIndex, pageSize, searchValue, sorting, filters, vertical)
    },
    getNextPageParam: (_lastGroup, groups) => {
      if (!Array.isArray(_lastGroup) || _lastGroup.length < pageSize) return undefined
      return groups.length
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const flatVerticalEntities: T[] | null = useMemo(() => {
    const flatEntities = verticalEntities?.pages.flatMap((page: T) => page) || null
    return flatEntities?.[0] ? flatEntities : null
  }, [verticalEntities])

  return {
    verticalEntities: flatVerticalEntities,
    fetchNextPage,
    isFetching,
    isLoading,
    searchValue,
    isError,
  }
}
